import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Copy, Eye } from 'lucide-react';
import axios from 'axios';
import RecordHistory from '../nativeitems/RecordHistory';
import RelatedRecords from '../nativeitems/RelatedRecords';
import AllFieldsPanel from './AllFieldsPanel';
import AttachmentsTab from './AttachmentsTab';
import { useRecordDetail, fmtQty, fmtMoney, fmtDate, yesNo } from './recordUtils';
import '../nativeitems/NativeItems.css';

// Work order record (Native, read-only) — tasks #62/#60. Header + production
// framing from work_orders_cache (Run's WO sync, incl. the promoted
// pour_completed / production window and the full ns_raw capture); component
// lines from Count's work_orders mirror. Links on to the legacy production
// detail (/workorders/:number) for batching/scheduling actions.

export default function WorkOrderRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const { data, loading, error } = useRecordDetail(`/api/native-records/work-orders/${encodeURIComponent(id)}`);

  if (loading) return <div className="nit-page"><div className="nit-loading">Loading work order…</div></div>;
  if (error) {
    return (
      <div className="nit-page">
        <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>
        <div className="nit-error">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { record, components, nativeWorkOrderId } = data;
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'components', label: 'Components', count: components.length },
    { key: 'fields', label: 'All fields' },
    { key: 'related', label: 'Related' },
    { key: 'history', label: 'History' },
    { key: 'attachments', label: 'Attachments' },
  ];

  return (
    <div className="nit-page">
      <button className="nit-btn nit-btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>

      <div className="nit-item-head" style={{ marginTop: 8 }}>
        <code>WO {record.work_order_number}</code>
        <h1>{record.item_description || record.item_sku || 'Work order'}</h1>
        <span className="nit-tag">{record.status || '—'}</span>
        {record.priority && <span className="nit-tag">{record.priority}</span>}
        {record.pour_completed === true && <span className="nit-tag nit-tag-assembly">pour completed</span>}
        {record.work_order_id && <span className="nit-tag">NS {record.work_order_id}</span>}
        <span className="nit-readonly"><Eye size={12} /> read-only</span>
        <Link to={`/native-work-orders/new?copyFrom=${encodeURIComponent(id)}`} className="nit-btn" style={{ marginLeft: 'auto', textDecoration: 'none' }}>
          <Copy size={14} /> Make Copy
        </Link>
        <button
          className="nit-btn"
          onClick={async () => {
            if (!window.confirm('Void this document? Every open line is closed on the NetSuite record (history is kept).')) return;
            try {
              const { data } = await axios.post(`/api/native-records/work-orders/${encodeURIComponent(id)}/void`);
              window.alert(data.note || 'Voided.');
              window.location.reload();
            } catch (err) {
              window.alert(err.response?.data?.error || err.message);
            }
          }}
        >
          Void
        </button>
      </div>

      <p className="nit-caption">
        Production actions (batching, completion, printing) live on the{' '}
        <Link to={`/workorders/${encodeURIComponent(record.work_order_number || '')}`}>production view</Link>.
      </p>

      <div className="nit-tabs">
        {tabs.map((t) => (
          <button key={t.key} className={`nit-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
            {t.count != null && <span className="nit-tab-count">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="nit-grid">
          <div className="nit-card">
            <h3>Build</h3>
            <div className="nit-facts">
              <span>Assembly</span>
              <strong>
                {record.item_sku
                  ? <Link to={`/native-items/${encodeURIComponent(record.item_sku)}`}><code>{record.item_sku}</code></Link>
                  : '—'}
              </strong>
              <span>Quantity ordered</span><strong>{fmtQty(record.quantity)}</strong>
              <span>Quantity built</span><strong>{fmtQty(record.quantity_built)}</strong>
              <span>Order date</span><strong>{fmtDate(record.order_date)}</strong>
              <span>Date required</span><strong>{fmtDate(record.date_scheduled)}</strong>
              <span>Memo</span><strong>{record.memo || '—'}</strong>
            </div>
          </div>

          <div className="nit-card">
            <h3>Production window</h3>
            <div className="nit-facts">
              <span>Pour completed</span><strong>{yesNo(record.pour_completed)}</strong>
              <span>Actual start</span><strong>{fmtDate(record.actual_production_start)}</strong>
              <span>Actual end</span><strong>{fmtDate(record.actual_production_end)}</strong>
              <span>Planned start</span><strong>{fmtDate(record.planned_start_date)}</strong>
              <span>Planned end</span><strong>{fmtDate(record.planned_end_date)}</strong>
            </div>
            {record.pour_completed == null && record.ns_raw == null && (
              <p className="nit-caption">Pour status + the production window arrive with the WO capture backfill (migration 20260705).</p>
            )}
          </div>

          <div className="nit-card">
            <h3>Recipe</h3>
            <div className="nit-facts">
              <span>Fragrance oil</span><strong>{record.fragrance_oil_name || '—'}</strong>
              <span>Vessel</span><strong>{record.vessel_name || record.vessel_sku || '—'}</strong>
              <span>Wick</span><strong>{record.wick || '—'}</strong>
              <span>Oz fill</span><strong>{fmtQty(record.oz_fill)}</strong>
              <span>Case qty</span><strong>{fmtQty(record.case_qty)}</strong>
              <span>Cart qty</span><strong>{fmtQty(record.cart_quantity)}</strong>
            </div>
          </div>
        </div>
      )}

      {tab === 'components' && (
        <div className="nit-card">
          <h3>Component lines</h3>
          {components.length === 0 && (
            <div className="nit-empty">
              No component lines in the Count mirror for this WO (the mirror covers transactions in
              its sync window).
            </div>
          )}
          {components.length > 0 && (
            <div className="nit-table-wrap">
              <table className="nit-table">
                <thead>
                  <tr><th>#</th><th>Component</th><th className="r">Quantity</th><th className="r">Unit cost</th><th className="r">Amount</th></tr>
                </thead>
                <tbody>
                  {components.map((c) => (
                    <tr key={c.id || c.line_number}>
                      <td className="nit-muted">{c.line_number}</td>
                      <td>{c.item_name || c.item_id}</td>
                      <td className="r">{fmtQty(c.quantity)}</td>
                      <td className="r nit-muted">{fmtMoney(c.unit_cost, 4)}</td>
                      <td className="r nit-muted">{fmtMoney(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'fields' && (
        <AllFieldsPanel
          family="work_order"
          nsRaw={record.ns_raw}
          overrides={{
            pourcompleted: record.pour_completed,
            custbody33: record.pour_completed,
            priority: record.priority,
          }}
        />
      )}

      {tab === 'related' && (
        nativeWorkOrderId
          ? <RelatedRecords recordType="work_order" id={nativeWorkOrderId} />
          : (
            <div className="nit-card">
              <h3>Related records</h3>
              <div className="nit-empty">
                No native twin for this WO yet — the Related graph runs on
                <code> native_work_orders</code> (demand pegs, snapshot BOM). NetSuite-only WOs link
                up as the native production flows take over.
              </div>
            </div>
          )
      )}

      {tab === 'history' && (
        nativeWorkOrderId
          ? <RecordHistory table="native_work_orders" rowId={nativeWorkOrderId} />
          : (
            <div className="nit-card">
              <h3>Field changes</h3>
              <div className="nit-empty">
                Change capture runs on the native WO tables; this mirror row has no native twin yet,
                so its field history remains in NetSuite (read-only).
              </div>
            </div>
          )
      )}

      {tab === 'attachments' && <AttachmentsTab recordType="work_order" recordId={id} />}
    </div>
  );
}
